import { Box, Grid, Stack, Typography } from "@mui/material";
import React from "react";

const normalizeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (Array.isArray(value)) {
    return JSON.stringify(value.map(normalizeValue).sort());
  }
  if (typeof value === "object") {
    if (value.id !== undefined) return value.id;
    return JSON.stringify(
      Object.keys(value)
        .sort()
        .reduce((result, key) => {
          result[key] = normalizeValue(value[key]);
          return result;
        }, {})
    );
  }
  return value;
};

const defaultRenderValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "boolean") return value ? "YES" : "NO";
  if (Array.isArray(value)) {
    if (!value.length) return "N/A";
    return value.map((item, index) => (
      <Box key={item?.id ?? index}>
        {typeof item === "object"
          ? item.name || item.business_name || item.email || JSON.stringify(item)
          : String(item)}
      </Box>
    ));
  }
  if (typeof value === "object") {
    return (
      value.name ||
      value.business_name ||
      value.email ||
      value.entity ||
      value.code ||
      JSON.stringify(value)
    );
  }
  return String(value);
};

const ComparisonColumn = ({
  data,
  otherData,
  fields,
  title,
  emptyMessage,
  side,
  greyedOut,
  highlightAll,
  disableHighlight,
}) => (
  <Grid item xs={12} md={6}>
    <Box
      sx={{
        height: "100%",
        p: 3,
        border: "1px solid",
        borderColor: greyedOut ? "grey.400" : "#999",
        borderRadius: 2,
        bgcolor: greyedOut ? "grey.100" : "background.paper",
        color: greyedOut ? "text.disabled" : "text.primary",
        opacity: greyedOut ? 0.65 : 1,
        filter: greyedOut ? "grayscale(1)" : "none",
      }}
    >
      <Typography variant="h6" sx={{ mb: 3 }}>
        {title}
      </Typography>
      {!data ? (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 1,
          }}
        >
          <Typography>{emptyMessage}</Typography>
        </Box>
      ) : (
        <Grid container spacing={1.5} alignItems="stretch">
          {fields.map((field) => {
            const value = field.getValue
              ? field.getValue(data)
              : data?.[field.key];
            const otherValue = field.getValue
              ? field.getValue(otherData)
              : otherData?.[field.key];
            const compareValue = field.compareValue || normalizeValue;
            const changed =
              !disableHighlight &&
              (highlightAll || compareValue(value) !== compareValue(otherValue));
            const background = side === "before" ? "#fff1f0" : "#f0fff4";
            const border = side === "before" ? "#ffccc7" : "#b7eb8f";

            return (
              <React.Fragment key={field.key}>
                <Grid
                  item
                  xs={5}
                  sx={{
                    p: 1,
                    bgcolor: changed ? background : "transparent",
                    borderLeft: changed
                      ? `3px solid ${border}`
                      : "3px solid transparent",
                  }}
                >
                  <Typography variant="subtitle1">{field.label}</Typography>
                </Grid>
                <Grid
                  item
                  xs={7}
                  sx={{
                    p: 1,
                    overflowWrap: "anywhere",
                    bgcolor: changed ? background : "transparent",
                    borderRight: changed
                      ? `3px solid ${border}`
                      : "3px solid transparent",
                  }}
                >
                  {field.render
                    ? field.render(value, data)
                    : defaultRenderValue(value)}
                </Grid>
              </React.Fragment>
            );
          })}
        </Grid>
      )}
    </Box>
  </Grid>
);

const PendingDetailsComparison = ({
  data,
  fields,
  original,
  greyBeforeOnUnsuspend = false,
}) => {
  const action = String(data?.action || "").trim().toLowerCase();
  const isAdd = action === "add";
  const isRemove = action === "remove";
  const isSuspend = action === "suspend";
  const isUnsuspend = action === "unsuspend";
  const isRestore = action === "restore";
  const useMainBefore = isRemove || isSuspend || isUnsuspend || isRestore;
  const before = isAdd ? null : useMainBefore ? data : original || null;
  const after = isRemove ? null : data;

  return (
    <>
      {!isRemove && (
        <Grid item xs={12}>
          <Stack direction="row" spacing={3}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: "#fff1f0",
                  border: "1px solid #ffccc7",
                }}
              />
              <Typography variant="body2">Changed before value</Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: "#f0fff4",
                  border: "1px solid #b7eb8f",
                }}
              />
              <Typography variant="body2">Changed after value</Typography>
            </Stack>
          </Stack>
        </Grid>
      )}
      <ComparisonColumn
        data={before}
        otherData={after}
        fields={fields}
        title="Before"
        emptyMessage="No existing record"
        side="before"
        greyedOut={isUnsuspend && greyBeforeOnUnsuspend}
        disableHighlight={isRemove}
      />
      <ComparisonColumn
        data={after}
        otherData={before}
        fields={fields}
        title="After"
        emptyMessage="Record will be removed"
        side="after"
        greyedOut={isSuspend}
        highlightAll={isAdd}
        disableHighlight={isRemove}
      />
    </>
  );
};

export default PendingDetailsComparison;
